import { replace, type RouteObject } from 'react-router'
import type { AdminFeature } from '@/features/admin'
import {
  createAdminPageLoader,
  createAdminViewLoader,
} from '@/features/admin/admin-route-loaders'
import type { WaitForAuthResolutionFn } from '@/features/iam'
import { links } from '@/utils/link'

/**
 * Options for {@link createRoutes}.
 */
export interface CreateAdminRoutesOptions {
  admin: AdminFeature
  waitForAuthResolution: WaitForAuthResolutionFn
  /**
   * Mounted under the service manager path, behind its permission.
   */
  serviceManagerRoutes: RouteObject[]
  /**
   * Mounted under the payouts path, behind its permission.
   */
  payoutsRoutes: RouteObject[]
}

/**
 * Creates routes for the admin feature.
 */
export function createRoutes(options: CreateAdminRoutesOptions): RouteObject[] {
  return [
    {
      path: '/admin',
      loader: createAdminViewLoader(options),
      lazy: {
        Component: () =>
          import('@/features/admin/Admin/AdminContainer').then(
            (r) => r.default,
          ),
      },
      children: [
        {
          index: true,
          loader: () => replace(links.adminChangeRequests()),
          Component: null,
        },
        {
          path: 'bookkeeping-manager/*',
          loader: createAdminPageLoader(
            options,
            (vm) => vm.hasBookkeepingPermission,
          ),
          lazy: {
            Component: () =>
              import('@/features/admin/Admin/AdminBookkeepingManagerContainer').then(
                (r) => r.default,
              ),
          },
        },
        {
          path: 'change-requests/*',
          loader: createAdminPageLoader(
            options,
            (vm) => vm.canManageJobScopeChangeRequest,
          ),
          lazy: {
            Component: () =>
              import('@/features/admin/Admin/AdminScopeChangeRequestsContainer').then(
                (r) => r.default,
              ),
          },
        },
        {
          path: 'service-manager',
          loader: createAdminPageLoader(options, (vm) => vm.canManageServices),
          children: options.serviceManagerRoutes,
        },
        {
          path: 'bundle-manager/*',
          loader: createAdminPageLoader(options, (vm) => vm.canManageServices),
          lazy: {
            Component: () =>
              import('@/features/admin/Admin/AdminBundleManagerContainer').then(
                (r) => r.default,
              ),
          },
        },
        {
          path: 'staffing-manager/*',
          loader: createAdminPageLoader(
            options,
            (vm) => vm.canSeeStaffingManager,
          ),
          lazy: {
            Component: () =>
              import('@/features/admin/Admin/AdminStaffingManagerContainer').then(
                (r) => r.default,
              ),
          },
        },
        {
          path: 'payouts',
          loader: createAdminPageLoader(options, (vm) => vm.canManagePayouts),
          children: options.payoutsRoutes,
        },
        {
          path: 'merge-requests/*',
          loader: createAdminPageLoader(
            options,
            (vm) => vm.canManageAccountMergeRequests,
          ),
          lazy: {
            Component: () =>
              import('@/features/admin/Admin/AdminMergeRequestsContainer').then(
                (r) => r.default,
              ),
          },
        },
        // Unknown admin paths keep the admin layout, with an empty page.
        { path: '*', Component: null },
      ],
    },
  ]
}
